import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";

export interface FilterSelectOption {
  label: string;
  value: string;
  /** Renders this option greyed-out and unclickable, e.g. already picked
   *  elsewhere. */
  disabled?: boolean;
}

interface FilterSelectProps {
  label: string;
  value: string;
  options: FilterSelectOption[];
  onChange: (value: string) => void;
  /** Label shown on the trigger when no option is selected */
  allLabel?: string;
  className?: string;
  disabled?: boolean;
  /**
   * "pill" (default): rounded-full chip used inline above list/table pages.
   * "boxed": bordered rectangular field with its own label on top, used in
   * expandable filter panels (matches the Search/Input field styling).
   */
  variant?: "pill" | "boxed";
  /** Boxed variant only — used when this doubles as a real form field. */
  required?: boolean;
  error?: boolean;
  helperText?: string;
}

/**
 * Single-select filter used above list/table pages (e.g. "Category",
 * "Status"). Not a form-field select — for that, use `Dropdown`.
 */
const FilterSelect: React.FC<FilterSelectProps> = ({
  label,
  value,
  options,
  onChange,
  allLabel = "All",
  className = "",
  disabled = false,
  variant = "pill",
  required = false,
  error = false,
  helperText,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selected = options.find((opt) => opt.value === value);
  const isBoxed = variant === "boxed";
  const displayLabel = selected ? selected.label : isBoxed ? allLabel : label;

  // Dropdown renders in a portal (so an ancestor's overflow/scroll clipping
  // can't cut it off) and flips above the trigger when there isn't enough
  // room below — same approach as SearchInput/Popover.
  const [coords, setCoords] = useState<{
    top: number;
    left: number;
    width?: number;
  } | null>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        ref.current &&
        !ref.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }

    const updatePosition = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      const dropdownHeight = dropdownRef.current?.offsetHeight ?? 0;
      const dropdownWidth =
        dropdownRef.current?.offsetWidth ?? (isBoxed ? rect.width : 192);
      const vh = window.innerHeight;
      const vw = window.innerWidth;
      const gap = 6;

      let top: number;
      if (
        rect.bottom + gap + dropdownHeight > vh &&
        rect.top - gap - dropdownHeight >= 0
      ) {
        top = rect.top - gap - dropdownHeight;
      } else {
        top = rect.bottom + gap;
      }

      let left: number;
      let width: number | undefined;
      if (isBoxed) {
        left = rect.left;
        width = rect.width;
      } else {
        // Pill variant's dropdown right-aligns to the trigger.
        left = rect.right - dropdownWidth;
        width = dropdownWidth;
      }
      left = Math.min(Math.max(8, left), vw - dropdownWidth - 8);

      setCoords({ top, left, width });
    };

    updatePosition();
    const raf = requestAnimationFrame(updatePosition);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isBoxed]);

  return (
    <div className={`relative ${isBoxed ? "w-full" : ""} ${className}`} ref={ref}>
      {isBoxed && (
        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          {label} {required && <span className="text-red-600">*</span>}
        </label>
      )}
      <button
        type="button"
        ref={triggerRef}
        disabled={disabled}
        title={disabled ? "Coming soon" : undefined}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={
          isBoxed
            ? `flex h-9 w-full items-center justify-between rounded-md border px-2 text-sm transition ${
                disabled
                  ? "cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-600"
                  : error
                  ? "border-red-500 bg-white text-gray-700 dark:bg-gray-800 dark:text-gray-200"
                  : "border-gray-300 bg-white text-gray-700 hover:border-gray-400 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-gray-600"
              }`
            : `flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition ${
                selected
                  ? "border-primary-200 bg-primary-50 text-primary-700 font-medium dark:border-primary-700 dark:bg-primary-900/40 dark:text-primary-300"
                  : "border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              }`
        }
      >
        <span className="truncate">{displayLabel}</span>
        <ChevronDown
          size={14}
          className={`shrink-0 ${open ? "rotate-180 transition-transform" : "transition-transform"}`}
        />
      </button>

      {open &&
        !disabled &&
        createPortal(
          <div
            ref={dropdownRef}
            className={`fixed z-50 max-h-64 overflow-y-auto rounded-xl border border-gray-100 bg-white p-1.5 shadow-lg dark:border-gray-700 dark:bg-gray-800 ${
              isBoxed ? "" : "w-48"
            }`}
            style={{
              top: coords?.top ?? -9999,
              left: coords?.left ?? -9999,
              width: coords?.width,
              visibility: coords ? "visible" : "hidden",
            }}
          >
            <button
              type="button"
              onClick={() => {
                onChange("");
                setOpen(false);
              }}
              className={`w-full rounded-lg px-3 py-2 text-left text-sm ${
                !value
                  ? "bg-gray-100 font-semibold text-gray-900 dark:bg-gray-700 dark:text-gray-100"
                  : "text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700/60"
              }`}
            >
              {allLabel}
            </button>
            {options.map((opt) => (
              <button
                type="button"
                key={opt.value}
                disabled={opt.disabled}
                title={opt.disabled ? "Already added in another line" : undefined}
                onClick={() => {
                  if (opt.disabled) return;
                  onChange(opt.value);
                  setOpen(false);
                }}
                className={`w-full rounded-lg px-3 py-2 text-left text-sm ${
                  opt.disabled
                    ? "cursor-not-allowed text-gray-300 dark:text-gray-600"
                    : value === opt.value
                    ? "bg-gray-100 font-semibold text-gray-900 dark:bg-gray-700 dark:text-gray-100"
                    : "text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700/60"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>,
          document.body
        )}

      {isBoxed && helperText && (
        <span
          className={`mt-1 block text-xs ${
            error ? "text-red-500" : "text-gray-500 dark:text-gray-400"
          }`}
        >
          {helperText}
        </span>
      )}
    </div>
  );
};

export default FilterSelect;
